import { useTranslation } from 'react-i18next';
import { useMemo, useCallback } from 'react';
import {
  formatDate,
  formatRelativeTime,
  getCurrentDate,
  getCurrentYear,
  formatDuration,
  toPersianDigits,
  toEnglishDigits,
  type DateFormat,
} from '../utils/dateUtils';

/**
 * Hook for date formatting with automatic language detection
 * Uses Jalali calendar for Persian and Gregorian for English
 */
export function useDate() {
  const { i18n } = useTranslation();
  const language = i18n.language;

  const format = useCallback(
    (date: Date | string | number, dateFormat: DateFormat = 'medium') => {
      return formatDate(date, language, dateFormat);
    },
    [language]
  );

  const relative = useCallback(
    (date: Date | string | number) => {
      return formatRelativeTime(date, language);
    },
    [language]
  );

  const duration = useCallback(
    (seconds: number) => {
      return formatDuration(seconds, language);
    },
    [language]
  );

  const toLocalDigits = useCallback(
    (num: number | string) => {
      if (language === 'fa') {
        return toPersianDigits(num);
      }
      return String(num);
    },
    [language]
  );

  const currentDate = useMemo(
    () => getCurrentDate(language),
    [language]
  );

  const currentYear = useMemo(
    () => getCurrentYear(language),
    [language]
  );

  return {
    format,
    relative,
    duration,
    toLocalDigits,
    currentDate,
    currentYear,
    toPersianDigits,
    toEnglishDigits,
    language,
  };
}
